import React, { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  TextField,
  Autocomplete,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Menu,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import {
  PARTIDAS_COM_FILTROS,
  PartidasComFiltrosData,
  PartidasComFiltrosVars,
  PartidaPorTorneio,
  PartidaFiltroInput,
} from '../../../services/partidaService';
import {
  CategoriaTorneio,
} from '../../../services/torneioService';
import CreateEditMatchModal, { AtletaSelecionavel } from '../../liga/categoryManagement/components/CreateEditMatchModal';
import { listarEquipesDoGrupo } from '../../../services/faseGrupoPartidaEquipeService';
import DefineScoreDialog from '../../../components/match/DefineScoreDialog';
import CancelScoreConfirmationDialog from '../../../components/match/CancelScoreConfirmationDialog';
import EditMatchFormatDialog from '../../../components/match/EditMatchFormatDialog';
import MatchSetsResult from '../../../components/match/MatchSetsResult';
import { atletasDoLado } from '../../../utils/atletasDoLado';

// Normaliza participantes de PartidaPorTorneio (null aninhado) para o shape ParticipanteLite
// esperado pelo CreateEditMatchModal (usuario/imagemUrl opcionais, sem null).
function normalizarParticipantes(
  ps?: PartidaPorTorneio['participantesA'],
): { id: string; usuario?: { id: string; nome?: string; username?: string; imagemUrl?: string } }[] {
  return (ps ?? []).map((p) => ({
    id: p.id,
    usuario: p.usuario
      ? {
          id: p.usuario.id,
          nome: p.usuario.nome,
          username: p.usuario.username,
          imagemUrl: p.usuario.imagemUrl ?? undefined,
        }
      : undefined,
  }));
}

interface TournamentMatchesTabProps {
  torneioId: string;
  categorias: CategoriaTorneio[];
  canEdit?: boolean;
}

interface Filters {
  categoriaId: string | null;
  faseId: string | null;
  dataInicio: string | null;
  dataFim: string | null;
  status: string | null;
  nomeAtleta: string;
}

type OrderBy = 'data' | null;
type Order = 'asc' | 'desc';

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '') : '';

const STATUS_OPTIONS = [
  { label: 'Todos', value: null },
  { label: 'Agendada', value: 'AGENDADA' },
  { label: 'Em Andamento', value: 'EM_ANDAMENTO' },
  { label: 'Finalizada', value: 'FINALIZADA' },
  { label: 'Cancelada', value: 'CANCELADA' },
];

const formatEndereco = (partida: PartidaPorTorneio): string => {
  if (!partida.local?.endereco) {
    return partida.local?.nome || '-';
  }
  const endereco = partida.local.endereco;
  const parts = [
    endereco.logradouro,
    endereco.numero,
    endereco.complemento,
    endereco.bairro,
    endereco.cidade?.nome,
    endereco.cidade?.estado?.uf,
  ].filter(Boolean);
  return parts.join(', ') || partida.local.nome || '-';
};


/**
 * Componente da aba "Partidas" do gerenciamento de torneio.
 * 
 * Exibe todas as partidas do torneio em uma tabela com filtros por categoria,
 * fase, data, status e nome do atleta.
 */
const TournamentMatchesTab: React.FC<TournamentMatchesTabProps> = ({
  torneioId,
  categorias,
  canEdit = true,
}) => {
  const [filters, setFilters] = useState<Filters>({
    categoriaId: null,
    faseId: null,
    dataInicio: null,
    dataFim: null,
    status: null,
    nomeAtleta: '',
  });

  const [editingPartida, setEditingPartida] = useState<PartidaPorTorneio | null>(null);
  const [showModal, setShowModal] = useState(false);
  // Pool de atletas do grupo da partida em edição (habilita editar participantes no rodízio).
  const [atletasDoGrupo, setAtletasDoGrupo] = useState<AtletaSelecionavel[]>([]);
  const [isRefetching, setIsRefetching] = useState(false);
  
  // Estados para dropdown de ações
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPartidaForMenu, setSelectedPartidaForMenu] = useState<PartidaPorTorneio | null>(null);
  
  // Estados para dialogs
  const [defineScoreDialogOpen, setDefineScoreDialogOpen] = useState(false);
  const [selectedPartidaForScore, setSelectedPartidaForScore] = useState<PartidaPorTorneio | null>(null);
  const [cancelScoreDialogOpen, setCancelScoreDialogOpen] = useState(false);
  const [selectedPartidaForCancel, setSelectedPartidaForCancel] = useState<PartidaPorTorneio | null>(null);
  const [editFormatDialogOpen, setEditFormatDialogOpen] = useState(false);
  const [selectedPartidaForFormat, setSelectedPartidaForFormat] = useState<PartidaPorTorneio | null>(null);

  // Preparar filtros para a query (filtros que são suportados pelo backend)
  const filtrosQuery: PartidaFiltroInput = useMemo(() => {
    const filtros: PartidaFiltroInput = {
      torneioId,
    };
    
    if (filters.categoriaId) {
      filtros.categoriaTorneioId = filters.categoriaId;
    }
    
    if (filters.status) {
      filtros.status = filters.status;
    }
    
    if (filters.dataInicio) {
      filtros.dataInicio = filters.dataInicio;
    }
    
    if (filters.dataFim) {
      filtros.dataFim = filters.dataFim;
    }
    
    return filtros;
  }, [torneioId, filters.categoriaId, filters.status, filters.dataInicio, filters.dataFim]);

  // Query para buscar partidas com filtros
  const { data, loading, error, refetch } = useQuery<PartidasComFiltrosData, PartidasComFiltrosVars>(
    PARTIDAS_COM_FILTROS,
    {
      variables: { filtros: filtrosQuery },
      skip: !torneioId,
      fetchPolicy: 'no-cache',
    }
  );

  const partidas = data?.partidasComFiltros || [];

  const [orderBy, setOrderBy] = useState<OrderBy>('data');
  const [order, setOrder] = useState<Order>('desc');

  // Opções de categoria para o Autocomplete
  const categoriaOptions = useMemo(() => {
    return categorias.map((cat) => ({
      id: cat.id,
      label: cat.nome,
    }));
  }, [categorias]);

  // Extrair fases únicas das partidas
  const faseOptions = useMemo(() => {
    const fasesMap = new Map<string, { id: string; label: string; ordem: number }>();
    partidas.forEach((partida) => {
      if (partida.faseCategoriaTorneio) {
        const fase = partida.faseCategoriaTorneio;
        if (!fasesMap.has(fase.id)) {
          fasesMap.set(fase.id, {
            id: fase.id,
            label: fase.nome,
            ordem: fase.ordem,
          });
        }
      }
    });
    return Array.from(fasesMap.values()).sort((a, b) => a.ordem - b.ordem);
  }, [partidas]);

  // Validação de datas
  const hasDateError = useMemo(() => {
    if (!filters.dataInicio || !filters.dataFim) return false;
    const inicio = dayjs(filters.dataInicio);
    const fim = dayjs(filters.dataFim);
    if (!inicio.isValid() || !fim.isValid()) return false;
    return inicio.isAfter(fim);
  }, [filters.dataInicio, filters.dataFim]);

  // Filtragem de partidas (client-side apenas para filtros que não são suportados pelo backend)
  // Nota: Filtros de categoria, status e data são feitos no backend via partidasComFiltros
  const filteredPartidas = useMemo(() => {
    return partidas.filter((partida) => {
      // Filtro por fase (não suportado pelo backend, fazer client-side)
      if (filters.faseId) {
        if (partida.faseCategoriaTorneio?.id !== filters.faseId) {
          return false;
        }
      }

      // Filtro por nome do atleta (não suportado pelo backend, fazer client-side)
      if (filters.nomeAtleta.trim()) {
        const todosAtletas = [
          ...atletasDoLado(partida, 'A'),
          ...atletasDoLado(partida, 'B'),
        ];
        const hasMatch = todosAtletas.some((atleta) =>
          atleta.nome.toLowerCase().includes(filters.nomeAtleta.toLowerCase()),
        );
        if (!hasMatch) return false;
      }

      return true;
    });
  }, [partidas, filters.faseId, filters.nomeAtleta]);

  // Ordenação
  const sortedPartidas = useMemo(() => {
    if (!orderBy) return filteredPartidas;

    return [...filteredPartidas].sort((a, b) => {
      let comparison = 0;

      if (orderBy === 'data') {
        // Tratar valores nulos: partidas sem data aparecem por último
        if (!a.data && !b.data) return 0;
        if (!a.data) return 1;
        if (!b.data) return -1;
        
        const dataA = dayjs(a.data).valueOf();
        const dataB = dayjs(b.data).valueOf();
        comparison = dataA - dataB;
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [filteredPartidas, orderBy, order]);

  const handleSort = (field: OrderBy) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder('desc'); // Começar com mais recente primeiro
    }
  };

  const handleResetFilters = () => {
    setFilters({
      categoriaId: null,
      faseId: null,
      dataInicio: null,
      dataFim: null,
      status: null,
      nomeAtleta: '',
    });
  };

  const handleFilterChange = useCallback((updates: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleEditarPartida = (partida: PartidaPorTorneio) => {
    setEditingPartida(partida);
    setShowModal(true);
    // Carrega o pool de atletas do grupo (rodízio) para permitir editar os participantes.
    const grupoId = partida.faseGrupoPartidas?.id;
    setAtletasDoGrupo([]);
    if (grupoId) {
      listarEquipesDoGrupo(grupoId)
        .then((vinculos) => {
          const mapa = new Map<string, AtletaSelecionavel>();
          (vinculos ?? []).forEach((v) =>
            (v?.equipe?.atletas ?? []).forEach((a) => {
              if (a?.id && !mapa.has(a.id)) mapa.set(a.id, a as AtletaSelecionavel);
            }),
          );
          setAtletasDoGrupo(Array.from(mapa.values()));
        })
        .catch(() => setAtletasDoGrupo([]));
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPartida(null);
    setAtletasDoGrupo([]);
  };

  const handleModalSuccess = async () => {
    handleCloseModal();
    setIsRefetching(true);
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  };

  // Handlers para dropdown de ações
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, partida: PartidaPorTorneio) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPartidaForMenu(partida);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPartidaForMenu(null);
  };

  const handleEditarFromMenu = () => {
    if (selectedPartidaForMenu) {
      handleEditarPartida(selectedPartidaForMenu);
    }
    handleMenuClose();
  };

  const handleDefinirResultado = () => {
    if (selectedPartidaForMenu) {
      setSelectedPartidaForScore(selectedPartidaForMenu);
      setDefineScoreDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleCancelarResultado = () => {
    if (selectedPartidaForMenu) {
      setSelectedPartidaForCancel(selectedPartidaForMenu);
      setCancelScoreDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleAlterarFormato = () => {
    if (selectedPartidaForMenu) {
      setSelectedPartidaForFormat(selectedPartidaForMenu);
      setEditFormatDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleFormatSuccess = async () => {
    setEditFormatDialogOpen(false);
    setSelectedPartidaForFormat(null);
    setIsRefetching(true);
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  };

  const handleDefineScoreSuccess = async () => {
    setDefineScoreDialogOpen(false);
    setSelectedPartidaForScore(null);
    setIsRefetching(true);
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  };

  const handleCancelScoreSuccess = async () => {
    setCancelScoreDialogOpen(false);
    setSelectedPartidaForCancel(null);
    setIsRefetching(true);
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  };

  if (loading || isRefetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Erro ao carregar partidas: {error.message}
      </Alert>
    );
  }

  return (
    <div className="mt-4">
      {/* Barra de Filtros */}
      <Box className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
          {/* Filtro por Categoria */}
          <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
            <Autocomplete
              options={categoriaOptions}
              getOptionLabel={(option) => option.label}
              value={categoriaOptions.find((opt) => opt.id === filters.categoriaId) || null}
              onChange={(_, value) => handleFilterChange({ categoriaId: value?.id || null })}
              renderInput={(params) => (
                <TextField {...params} label="Categoria" size="small" />
              )}
            />
          </Box>

          {/* Filtro por Fase */}
          <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
            <Autocomplete
              options={faseOptions}
              getOptionLabel={(option) => option.label}
              value={faseOptions.find((opt) => opt.id === filters.faseId) || null}
              onChange={(_, value) => handleFilterChange({ faseId: value?.id || null })}
              renderInput={(params) => (
                <TextField {...params} label="Fase" size="small" />
              )}
            />
          </Box>

          {/* Filtro por Nome do Atleta */}
          <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
            <TextField
              label="Nome do Atleta"
              size="small"
              fullWidth
              value={filters.nomeAtleta}
              onChange={(e) => handleFilterChange({ nomeAtleta: e.target.value })}
              placeholder="Buscar por nome"
            />
          </Box>

          {/* Filtro por Status */}
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={filters.status || ''}
              onChange={(e) =>
                handleFilterChange({ status: e.target.value || null })
              }
              SelectProps={{
                displayEmpty: true,
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value || ''}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Filtro por Data Início */}
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Início"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.dataInicio || ''}
              onChange={(e) => handleFilterChange({ dataInicio: e.target.value || null })}
              error={hasDateError}
              helperText={hasDateError ? 'Data início maior que data fim' : undefined}
            />
          </Box>

          {/* Filtro por Data Fim */}
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Fim"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.dataFim || ''}
              onChange={(e) => handleFilterChange({ dataFim: e.target.value || null })}
              error={hasDateError}
              helperText={hasDateError ? 'Data início maior que data fim' : undefined}
            />
          </Box>

          {/* Botões */}
          <Box sx={{ flex: '0 1 auto' }}>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={handleResetFilters}
              >
                Limpar
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Tabela de Partidas */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#6c757d' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                Categoria, Data e Local
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                Fase, Grupo e Nº Partida
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                Equipe A
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                Equipe B
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'data'}
                  direction={orderBy === 'data' ? order : 'asc'}
                  onClick={() => handleSort('data')}
                  style={{ color: 'inherit' }}
                >
                  Resultado
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPartidas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {partidas.length === 0
                      ? 'Nenhuma partida encontrada para este torneio.'
                      : 'Nenhuma partida corresponde aos filtros aplicados.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedPartidas.map((partida) => (
                <TableRow key={partida.id} hover>
                  {/* Coluna 1: Categoria, Data e Local */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {partida.categoriaTorneio?.nome || 'Sem categoria'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(partida.data)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {formatEndereco(partida)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Coluna 2: Fase, Grupo e Nº Partida */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {partida.faseCategoriaTorneio?.nome || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {partida.faseGrupoPartidas?.nome || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Partida #{partida.numeroPartida}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Coluna 3: Atletas da Equipe A (ou participantes do lado A no rodízio) */}
                  <TableCell>
                    {(() => {
                      const atletas = atletasDoLado(partida, 'A');
                      return atletas.length > 0 ? (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {atletas.map((atleta) => (
                            <Box key={atleta.id} display="flex" alignItems="center" gap={1}>
                              {atleta.imagemUrl ? (
                                <Avatar
                                  src={atleta.imagemUrl}
                                  alt={atleta.nome}
                                  sx={{ width: 24, height: 24 }}
                                />
                              ) : (
                                <Avatar sx={{ width: 24, height: 24 }}>
                                  {atleta.nome.charAt(0).toUpperCase() || '?'}
                                </Avatar>
                              )}
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {atleta.nome || 'Sem nome'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      );
                    })()}
                  </TableCell>

                  {/* Coluna 4: Atletas da Equipe B (ou participantes do lado B no rodízio) */}
                  <TableCell>
                    {(() => {
                      const atletas = atletasDoLado(partida, 'B');
                      return atletas.length > 0 ? (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {atletas.map((atleta) => (
                            <Box key={atleta.id} display="flex" alignItems="center" gap={1}>
                              {atleta.imagemUrl ? (
                                <Avatar
                                  src={atleta.imagemUrl}
                                  alt={atleta.nome}
                                  sx={{ width: 24, height: 24 }}
                                />
                              ) : (
                                <Avatar sx={{ width: 24, height: 24 }}>
                                  {atleta.nome.charAt(0).toUpperCase() || '?'}
                                </Avatar>
                              )}
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {atleta.nome || 'Sem nome'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      );
                    })()}
                  </TableCell>

                  {/* Coluna 5: Resultado */}
                  <TableCell>
                    <MatchSetsResult
                      placar={partida.placar}
                      equipeAWO={partida.equipeAWO}
                      equipeBWO={partida.equipeBWO}
                      equipeADesistencia={partida.equipeADesistencia}
                      equipeBDesistencia={partida.equipeBDesistencia}
                      useMaterialUI={true}
                    />
                  </TableCell>

                  {/* Coluna 6: Ações */}
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => handleMenuOpen(e, partida)}
                      sx={{ minWidth: '100px' }}
                    >
                      Opções
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Informação de total */}
      {sortedPartidas.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Total de partidas: {sortedPartidas.length}
            {partidas.length !== sortedPartidas.length &&
              ` de ${partidas.length} (filtradas)`}
          </Typography>
        </Box>
      )}

      {/* Menu Dropdown de Ações */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEditarFromMenu}>
          Editar
        </MenuItem>
        <MenuItem onClick={handleAlterarFormato}>
          Alterar Formato
        </MenuItem>
        {selectedPartidaForMenu && 
          selectedPartidaForMenu.placar?.resultadoFinalA == null &&
          selectedPartidaForMenu.placar?.resultadoFinalB == null && (
          <MenuItem onClick={handleDefinirResultado}>
            Definir resultado
          </MenuItem>
        )}
        {selectedPartidaForMenu &&
          selectedPartidaForMenu.placar?.resultadoFinalA != null &&
          selectedPartidaForMenu.placar?.resultadoFinalB != null && (
          <MenuItem onClick={handleCancelarResultado}>
            Cancelar Resultado
          </MenuItem>
        )}
      </Menu>

      {/* Modal de Edição */}
      {showModal && editingPartida && (
        <CreateEditMatchModal
          partida={{
            id: editingPartida.id,
            numeroPartida: editingPartida.numeroPartida,
            data: editingPartida.data || '',
            local: editingPartida.local,
            equipeA: editingPartida.equipeA,
            equipeB: editingPartida.equipeB,
            participantesA: normalizarParticipantes(editingPartida.participantesA),
            participantesB: normalizarParticipantes(editingPartida.participantesB),
            placar: editingPartida.placar,
            status: editingPartida.status,
            atualizadoEm: editingPartida.atualizadoEm || '',
            criadoEm: editingPartida.criadoEm || '',
          }}
          categoriaId={editingPartida.categoriaTorneio?.id || ''}
          faseCategoriaTorneioId={editingPartida.faseCategoriaTorneio?.id || ''}
          grupoId={editingPartida.faseGrupoPartidas?.id}
          atletasDoGrupo={atletasDoGrupo}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
          canEdit={canEdit}
        />
      )}

      {/* Dialog Definir Resultado */}
      {defineScoreDialogOpen && selectedPartidaForScore && (
        <DefineScoreDialog
          open={defineScoreDialogOpen}
          partidaId={selectedPartidaForScore.id}
          placarAtual={selectedPartidaForScore.placar}
          numeroSets={selectedPartidaForScore.numeroSets ?? selectedPartidaForScore.categoriaTorneio?.numeroSets ?? 3}
          gamesPorSets={selectedPartidaForScore.gamesPorSets ?? selectedPartidaForScore.categoriaTorneio?.gamesPorSets ?? null}
          tiebreaks={selectedPartidaForScore.tiebreaks ?? selectedPartidaForScore.categoriaTorneio?.tiebreaks ?? null}
          categoriaTorneioId={selectedPartidaForScore.categoriaTorneio?.id}
          categoriaTorneio={selectedPartidaForScore.categoriaTorneio ? {
            numeroSets: selectedPartidaForScore.categoriaTorneio.numeroSets ?? null,
            gamesPorSets: selectedPartidaForScore.categoriaTorneio.gamesPorSets ?? null,
            tiebreaks: selectedPartidaForScore.categoriaTorneio.tiebreaks ?? null,
          } : null}
          onClose={() => {
            setDefineScoreDialogOpen(false);
            setSelectedPartidaForScore(null);
          }}
          onSuccess={handleDefineScoreSuccess}
        />
      )}

      {/* Dialog Cancelar Resultado */}
      {cancelScoreDialogOpen && selectedPartidaForCancel && (
        <CancelScoreConfirmationDialog
          open={cancelScoreDialogOpen}
          placarId={selectedPartidaForCancel.placar?.id || ''}
          onClose={() => {
            setCancelScoreDialogOpen(false);
            setSelectedPartidaForCancel(null);
          }}
          onSuccess={handleCancelScoreSuccess}
        />
      )}

      {/* Dialog Alterar Formato */}
      {editFormatDialogOpen && selectedPartidaForFormat && (
        <EditMatchFormatDialog
          open={editFormatDialogOpen}
          partidaId={selectedPartidaForFormat.id}
          placarAtual={selectedPartidaForFormat.placar}
          numeroSets={selectedPartidaForFormat.numeroSets}
          gamesPorSets={selectedPartidaForFormat.gamesPorSets}
          tiebreaks={selectedPartidaForFormat.tiebreaks}
          onClose={() => {
            setEditFormatDialogOpen(false);
            setSelectedPartidaForFormat(null);
          }}
          onSuccess={handleFormatSuccess}
        />
      )}
    </div>
  );
};

export default TournamentMatchesTab;
